import { Component, OnInit, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatFormFieldModule, MatInputModule } from '@angular/material';
import { User, Region, Region15 } from '../user.model';
import { UserDetailsDialog } from '../user-details/user-details-dialog.model';
import { Team } from '../../teams/team.model';
import { Role } from '../../roles/role.model';
import { Persona } from '../../personas/persona.model';
import { PersonaService } from '../../personas/persona.service';
import { TeamService } from '../../teams/team.service';
import { UserService } from '../user.service';
import { RoleService } from '../../roles/role.service';
import { UserEditComponent } from '../user-edit/user-edit.component';
import { LoanProgramService } from '../../loanprograms/loanprogram.service';

@Component({
    selector: 'app-user-add',
    templateUrl: './user-add.component.html',
    styleUrls: ['./user-add.component.css']
})
export class UserAddComponent implements OnInit {
    public userForm: FormGroup;
    public user: User;
    public personas: Persona[];
    public teams: Team[];
    public regions: Region[];
    public filteredRegions: Region[];
    public roles: Role[];
    public activeTab: string;
    public encompassUserNameValue: string;
    public teamEmail: string;
    public whiteGloveOptions: string[];

    public isRegion15 = false;

    constructor(
        public dialogRef: MatDialogRef<UserAddComponent>,
        private fb: FormBuilder,
        private personaService: PersonaService,
        private teamService: TeamService,
        private userService: UserService,
        private roleService: RoleService,
        private loanProgramService: LoanProgramService,
        public dialog: MatDialog) {
    }

    ngOnInit() {
        this.getTeams();
        this.getRoles();
        this.getPersonas();
        this.getRegions();
        this.getWhiteGloveOptions();
        this.createForm();
    }

    getRegions() {
        this.loanProgramService.getRegionAssignmentStrategies().then(
            result => {
                result.sort((a, b) => a.name.localeCompare(b.name));
                this.regions = JSON.parse(JSON.stringify(result));
            }
        )
    }

    getRegionsByGroup(groupName: string) {
        this.loanProgramService.getRegionAssignmentStrategiesByGroupName(groupName).then(
            result => {
                result.sort((a, b) => a.name.localeCompare(b.name));
                this.filteredRegions = JSON.parse(JSON.stringify(result));
            }
        )
    }



    saveRegion(user: User) {
        const formModel = this.userForm.value;
        var value = formModel.filteredRegions;
        var selectedRegion = this.filteredRegions ? this.filteredRegions.find(x => x.id === value) : null;

        if (!selectedRegion) {
            selectedRegion = this.regions ? this.regions.find(x => x.id === value) : null;
        }

        if (selectedRegion != undefined && user.id != undefined) {
            this.userService.addUserRegion(user.id, selectedRegion.id);
        }
    }

    getPersonas() {
        this.personaService.getPersonasCache()
            .then(resp => {
                this.personas = this.filterOutOldPersonas(resp);
            })
    }

    getTeams() {
        this.teamService.getTeamsCache()
            .then(resp => {
                this.teams = resp;
            });
    }

    getRoles() {
        this.roleService.getRolesCache()
            .then(resp => {
                this.roles = resp;
            });
    }

    getWhiteGloveOptions() {
        this.whiteGloveOptions = this.userService.getWhiteGloveOptions();
    }

    getLoggedInUser() {
        var loggedInUser = this.userService.currentUser;
        return loggedInUser;
    }

    filterOutOldPersonas(personas: Persona[]) {
        var outdatedPersonas = ['Quick Approval UW', 'Fast Track UW'];

        return personas.filter(x => !outdatedPersonas.includes(x.name));
    }

    isCapacityEnabledPersona() {
        if (this.personas) {
            var underwriterPersonas = this.personas.filter(x => x.name === "Underwriter" || x.name === "UW 1" || x.name === "Collateral UW" || x.name === "Fast Track UW" || x.name === "Quick Approval UW");
            return underwriterPersonas.some(x => x.id === this.userForm.value.persona);
        }
    }

    isTeamEmailEnabledPersona() {
        if (this.personas) {
            var loanOfficerPersona = this.personas.find(x => x.name === "Loan Officer");
            return this.userForm.value.persona == loanOfficerPersona.id;
        }
    }

    private nullOrEmailValidator(control: AbstractControl): ValidationErrors {
        if (!control.value) {
            return null;
        }

        return Validators.email(control);
    }

    createForm() {
        this.userForm = this.fb.group({
            firstName: [null, Validators.required],
            lastName: [null, Validators.required],
            persona: [null, Validators.required],
            teams: [null, Validators.required],
            filteredRegions: [null, Validators.nullValidator],
            role: [null, Validators.required],
            encompassUserName: [null, Validators.required],
            teamEmail: [null, this.nullOrEmailValidator],
            email: [null, Validators.compose([Validators.required, Validators.email])],
            capacity: [null, Validators.compose([Validators.nullValidator, Validators.pattern('[0-9]*')])],
            whiteGlove: [null, Validators.required],
        });
    }

    onWhiteGloveSelectionChange(event) {
        if (event.value == "Region 15") {
            this.isRegion15 = true;
        }
        else {
            this.isRegion15 = false;
        }
    }

    onPersonaSelectionChange(event) {
        if (this.GetPersonaName(event.value) == "Loan Officer") {
            this.getRegionsByGroup("Underwriting");
        }
        else if (this.ShouldUserHaveRegion()) {
            this.getRegionsByGroup("Closer");
        }

        this.updateValidators(event.value);
    }

    updateValidators(id: string) {
        if (this.GetPersonaName(id) == "Loan Officer") {
            this.userForm.controls['filteredRegions'].setValidators([Validators.required]);
        }
        else {
            this.userForm.controls['filteredRegions'].clearValidators();
        }
        this.userForm.controls['filteredRegions'].updateValueAndValidity();
    }

    onSubmit() {
        if (!this.IsUserValidLoanOfficer()) {
            this.userForm.controls['whiteGlove'].setValue('N/A');
        }

        if (!this.userForm.valid)
            return;
        this.user = this.prepareSaveUser();
        this.userService.addUser(this.user)
            .then(resp => {
                var userDetailDialog: UserDetailsDialog = {
                    user: resp,
                    activeTab: this.activeTab
                };

                this.saveRegion(resp);

                this.dialog.open(UserEditComponent, {
                    data: userDetailDialog,
                    width: "800px",
                    height: "600px"
                });
                this.dialogRef.close();
            }).catch(err => {
                confirm("There was an error adding the new partner. Does a partner with this email already exist in the system?");
            })
    }

    prepareSaveUser(): User {
        const formModel = this.userForm.value;
        var saveUser: User = new User();

        var newRegion15 = new Region15;
        newRegion15.option = "";

        saveUser.firstName = formModel.firstName;
        saveUser.lastName = formModel.lastName;
        saveUser.personaId = formModel.persona;
        saveUser.roleId = formModel.role;
        saveUser.encompassUserName = formModel.encompassUserName;
        saveUser.teamEmail = formModel.teamEmail;
        saveUser.email = formModel.email;
        saveUser.teamIds = formModel.teams;
        saveUser.isUnavailable = false;
        saveUser.hasSpecialties = false;
        saveUser.capacity = formModel.capacity;
        saveUser.loggedInUserEmail = this.getLoggedInUser().email;
        saveUser.whiteGlove = formModel.whiteGlove;
        saveUser.region15 = newRegion15;
        return saveUser;
    }

    ConvertToLower(event) {
        if (event) {
            this.encompassUserNameValue = event.toLowerCase();
        }
    }

    GetPersonaName(id: string) {
        var personaName = "";
        if (this.personas) {
            var persona = this.personas.find(x => x.id == id);
            personaName = persona.name;
        }

        return personaName
    }


    ShouldUserHaveRegion() {
        var isValid = false;

        if (this.personas) {
            var srCloser = this.personas.find(x => x.name === "Sr Closer");
            var icdCloser = this.personas.find(x => x.name === "ICD Closer");
            var loanOfficer = this.personas.find(x => x.name == "Loan Officer");

            isValid = this.userForm.value.persona == srCloser.id || this.userForm.value.persona == icdCloser.id || this.userForm.value.persona == loanOfficer.id;
        }

        return isValid;
    }

    IsUserValidLoanOfficer() {
        var isValid = false;

        if (this.personas) {
            var loanOfficer = this.personas.find(x => x.name === "Loan Officer");
            isValid = this.userForm.value.persona == loanOfficer.id;
        }

        return isValid;
    }

}



// WEBPACK FOOTER //
// D:/a/1/s/Uhm.Loops.Web/src/app/users/user-add/user-add.component.ts